/**
 * RefundRequestPanel — self-serve refund UI.
 *
 * Pulls the user's current `/api/content-protection/refund-eligibility` +
 * `/api/content-protection/my-refund-requests`. If eligible and no pending
 * request exists, renders a form to submit one. If not eligible, explains
 * why (outside window OR > 50% course consumption).
 *
 * Drop this component anywhere on the user profile / billing page.
 */
import React, { useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Wallet, Clock, Check, X, AlertTriangle } from 'lucide-react';

const REASON_PROMPTS = {
  outside_window: {
    icon: Clock,
    title: 'Your 14-day refund window has passed',
    body: 'Refunds within 14 days of a fresh subscription are honored automatically. Outside that window, contact support via the Help Center — we still handle special cases in good faith.',
  },
  consumption_threshold_exceeded: {
    icon: AlertTriangle,
    title: 'Refund entitlement voided',
    body: "You've completed more than 50% of a course during the refund window. As stated in the Terms of Use, this voids the refund entitlement. You keep full access to everything you've paid for.",
  },
  eligible: {
    icon: Check,
    title: "You're eligible for a refund",
    body: "You're still inside your 14-day window and under the 50% consumption threshold. Submit a request below and a moderator will review it within one business day.",
  },
};

const RefundRequestPanel = ({ className = '' }) => {
  const { API } = useContext(AuthContext);
  const [eligibility, setEligibility] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [elig, mine] = await Promise.all([
        axios.get(`${API}/content-protection/refund-eligibility`),
        axios.get(`${API}/content-protection/my-refund-requests`),
      ]);
      setEligibility(elig.data);
      setRequests(mine.data?.requests || []);
    } catch (err) {
      toast.error('Could not load refund status.');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/content-protection/request-refund`, { reason });
      toast.success('Refund request submitted. We\u2019ll get back to you within 1 business day.');
      setReason('');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit request.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card className={className} data-testid="refund-panel-loading">
        <CardContent className="p-6 text-sm text-muted-foreground">Loading refund status…</CardContent>
      </Card>
    );
  }

  const pending = requests.find((r) => r.status === 'pending');
  const promptKey = eligibility?.reason || 'outside_window';
  const prompt = REASON_PROMPTS[promptKey] || REASON_PROMPTS.outside_window;
  const Icon = prompt.icon;

  return (
    <Card className={className} data-testid="refund-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wallet className="w-5 h-5" /> Refund request
        </CardTitle>
        <CardDescription>Self-serve refund inside your 14-day window.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          className={`p-4 rounded-xl border ${
            eligibility?.eligible
              ? 'bg-primary/5 border-primary/40'
              : 'bg-muted/40 border-border'
          }`}
          data-testid="refund-status-card"
        >
          <div className="flex items-start gap-3">
            <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${eligibility?.eligible ? 'text-primary' : 'text-muted-foreground'}`} />
            <div className="flex-1">
              <h4 className="text-sm font-heading font-extrabold text-foreground">{prompt.title}</h4>
              <p className="text-xs text-foreground/85 leading-relaxed mt-1">{prompt.body}</p>
              {typeof eligibility?.consumption_pct === 'number' && eligibility?.threshold_pct && (
                <div className="mt-2 text-[11px] text-muted-foreground">
                  Consumption: <strong>{eligibility.consumption_pct}%</strong> of {eligibility.threshold_pct}% cap ·
                  {' '}Days remaining: <strong>{eligibility.days_remaining}</strong>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pending state */}
        {pending && (
          <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-300/60 text-xs" data-testid="pending-request">
            <div className="flex items-center gap-2 font-bold text-amber-900 dark:text-amber-200">
              <Clock className="w-4 h-4" /> Request pending review
            </div>
            <div className="text-amber-900/85 dark:text-amber-100/90 mt-1">
              Submitted {new Date(pending.created_at).toLocaleString()}. A moderator will respond within 1 business day.
            </div>
          </div>
        )}

        {/* Submit form — only when eligible AND no pending request */}
        {eligibility?.eligible && !pending && (
          <div className="space-y-2">
            <label className="text-xs font-bold text-foreground">Why are you requesting a refund? (optional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={1000}
              className="w-full text-sm p-3 rounded-lg border border-input bg-background"
              placeholder="Anything you tell us helps us improve the platform."
              data-testid="refund-reason-input"
            />
            <div className="flex justify-end">
              <Button
                onClick={submit}
                disabled={submitting}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="submit-refund-btn"
              >
                {submitting ? 'Submitting…' : 'Submit refund request'}
              </Button>
            </div>
          </div>
        )}

        {/* History */}
        {requests.length > 0 && (
          <div className="pt-3 border-t border-border" data-testid="refund-history">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">History</div>
            <ul className="space-y-1.5">
              {requests.map((r) => (
                <li key={r.id} className="text-xs flex items-center gap-2 justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge
                      variant={r.status === 'approved' ? 'default' : r.status === 'denied' ? 'outline' : 'secondary'}
                      className="text-[10px] uppercase"
                    >
                      {r.status === 'approved' ? <Check className="w-2.5 h-2.5 mr-0.5" /> : r.status === 'denied' ? <X className="w-2.5 h-2.5 mr-0.5" /> : null}
                      {r.status}
                    </Badge>
                    <span className="text-muted-foreground truncate">
                      {new Date(r.created_at).toLocaleDateString()} · {r.tier_name || '—'}
                    </span>
                  </div>
                  {r.resolution_note && (
                    <span className="text-[10px] text-muted-foreground italic truncate max-w-[50%]">
                      &ldquo;{r.resolution_note}&rdquo;
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RefundRequestPanel;
